import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createDiary, updateDiary, getDiaryById } from "../api/diaryApi";
import { getSymptoms } from "../api/symptomApi";
import type { Symptom } from "../types/symptom";
import type { CreateDiaryRequest, UpdateDiaryRequest } from "../types/diary";
import { useLanguage } from "../i18n/LanguageContext";

function toDateTimeLocalInputValue(date: Date): string {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
}

function utcIsoToLocalInputValue(utcIso: string): string {
  return toDateTimeLocalInputValue(new Date(utcIso));
}

function localInputValueToUtcIso(localValue: string): string {
  return new Date(localValue).toISOString();
}

function parseNumberInput(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function DiaryFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);

  const [form, setForm] = useState<CreateDiaryRequest>({
    dateTime: toDateTimeLocalInputValue(new Date()),
    energyLevel: 5,
    activities: "",
    pauses: "",
    postExertionalMalaise: false,
    sleepNotes: "",
    otherNotes: "",
    symptomIds: [],
  });

  useEffect(() => {
    async function loadSymptoms() {
      try {
        const data = await getSymptoms();
        setSymptoms(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load symptoms");
      }
    }

    loadSymptoms();
  }, []);

  function toggleSymptom(symptomId: number) {
    setForm((prev) => {
      const selectedIds = prev.symptomIds ?? [];
      const isSelected = selectedIds.includes(symptomId);

      return {
        ...prev,
        symptomIds: isSelected
          ? selectedIds.filter((id) => id !== symptomId)
          : [...selectedIds, symptomId],
      };
    });
  }

  useEffect(() => {
    if (!isEdit) return;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        const diary = await getDiaryById(Number(id));
        setForm({
          dateTime: utcIsoToLocalInputValue(diary.dateTime),
          energyLevel: diary.energyLevel,
          activities: diary.activities ?? "",
          pauses: diary.pauses ?? "",
          postExertionalMalaise: diary.postExertionalMalaise,
          sleepNotes: diary.sleepNotes ?? "",
          otherNotes: diary.otherNotes ?? "",
          symptomIds: diary.symptomIds ?? [],
          peakFlowReadingBeforeSpray:
            diary.peakFlowReadingBeforeSpray ?? undefined,
          peakFlowReadingAfterSpray:
            diary.peakFlowReadingAfterSpray ?? undefined,
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load diary");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id, isEdit]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const payloadDateTimeUtc = localInputValueToUtcIso(form.dateTime);

      if (isEdit) {
        const update: UpdateDiaryRequest = {
          ...form,
          dateTime: payloadDateTimeUtc,
        };
        await updateDiary(Number(id), update);
      } else {
        const createRequest: CreateDiaryRequest = {
          ...form,
          dateTime: payloadDateTimeUtc,
        };
        await createDiary(createRequest);
      }

      navigate("/diaries");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save diary");
      setLoading(false);
    }
  }

  function handleChange(
    field: keyof CreateDiaryRequest,
    value: CreateDiaryRequest[keyof CreateDiaryRequest],
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  if (loading && !form) return <div>{t.diaryForm.loading}</div>;
  if (error) return <div>{error}</div>;

  return (
    <main className="diary-page">
      <form className="diary-form-card" onSubmit={handleSubmit}>
        <h1>{isEdit ? t.diaryForm.titleEdit : t.diaryForm.titleNew}</h1>

        {error && <div className="form-error">{error}</div>}

        <div className="form-field">
          <label htmlFor="dateTime">{t.diaryForm.dateTime}</label>
          <input
            id="dateTime"
            type="datetime-local"
            value={form.dateTime}
            onChange={(e) => handleChange("dateTime", e.target.value)}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="energyLevel">{t.diaryForm.energyLevel}</label>
          <input
            id="energyLevel"
            type="number"
            min={1}
            max={10}
            value={form.energyLevel}
            onChange={(e) =>
              handleChange("energyLevel", Number(e.target.value))
            }
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="peakFlowReadingBeforeSpray">
            {t.diaryForm.peakFlowBeforeSpray}
          </label>
          <input
            id="peakFlowReadingBeforeSpray"
            type="number"
            value={form.peakFlowReadingBeforeSpray ?? ""}
            onChange={(e) =>
              handleChange(
                "peakFlowReadingBeforeSpray",
                parseNumberInput(e.target.value),
              )
            }
          />
        </div>

        <div className="form-field">
          <label htmlFor="peakFlowReadingAfterSpray">
            {t.diaryForm.peakFlowAfterSpray}
          </label>
          <input
            id="peakFlowReadingAfterSpray"
            type="number"
            value={form.peakFlowReadingAfterSpray ?? ""}
            onChange={(e) =>
              handleChange(
                "peakFlowReadingAfterSpray",
                parseNumberInput(e.target.value),
              )
            }
          />
        </div>

        <section className="symptoms-section">
          <p>{t.diaryForm.symptoms}</p>

          {symptoms.length === 0 ? (
            <p>{t.diaryForm.noSymptoms}</p>
          ) : (
            symptoms.map((symptom) => (
              <label className="symptom-option" key={symptom.id}>
                <input
                  type="checkbox"
                  checked={(form.symptomIds ?? []).includes(symptom.id)}
                  onChange={() => toggleSymptom(symptom.id)}
                />

                <strong>{symptom.name}</strong>

                {symptom.description && (
                  <span className="symptom-description">
                    {symptom.description}
                  </span>
                )}
              </label>
            ))
          )}
        </section>

        <div className="form-field">
          <label htmlFor="activities">{t.diaryForm.activities}</label>
          <textarea
            id="activities"
            value={form.activities}
            onChange={(e) => handleChange("activities", e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="pauses">{t.diaryForm.pauses}</label>
          <textarea
            id="pauses"
            value={form.pauses}
            onChange={(e) => handleChange("pauses", e.target.value)}
          />
        </div>

        <div className="checkbox-field">
          <input
            id="postExertionalMalaise"
            type="checkbox"
            checked={form.postExertionalMalaise}
            onChange={(e) =>
              handleChange("postExertionalMalaise", e.target.checked)
            }
          />

          <label htmlFor="postExertionalMalaise">
            {t.diaryForm.postExertionalMalaise}
          </label>
        </div>

        <div className="form-field">
          <label htmlFor="sleepNotes">{t.diaryForm.sleepNotes}</label>
          <textarea
            id="sleepNotes"
            value={form.sleepNotes}
            onChange={(e) => handleChange("sleepNotes", e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="otherNotes">{t.diaryForm.otherNotes}</label>
          <textarea
            id="otherNotes"
            value={form.otherNotes}
            onChange={(e) => handleChange("otherNotes", e.target.value)}
          />
        </div>

        <div className="form-actions">
          <button
            className="secondary-button"
            type="button"
            onClick={() => navigate("/diaries")}
            disabled={loading}
          >
            {t.diaryForm.cancel}
          </button>

          <button className="primary-button" type="submit" disabled={loading}>
            {loading
              ? t.diaryForm.saving
              : isEdit
                ? t.diaryForm.saveChanges
                : t.diaryForm.createEntry}
          </button>
        </div>
      </form>
    </main>
  );
}
